import React from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, Box, TextFieldForm, nameRegExp } from 'web-library';
import { UseFormReturn } from 'react-hook-form';
import { fallbackDashString } from '../../../../utils/StringUtils';
import { getContactDisplayValue } from '../../../../utils/ComponentUtils';
import { MobileNumberItems } from '../../../../api/candidate/profile/@candidate/models';
import { useStyles } from './style';
import ToggleDisplayEditFormItem from '../../../../components/ToggleDisplayEditFormItem';
import { disabledInput } from '../../logic/useUpdateRegistrationParticulars';
import ControlledAutocomplete from '../../../../components/form-items/ControlledAutocomplete';
import {
  DROPDOWN_PLACEHOLDER,
  getIExamDropdownItemLabel,
  getIExamMasterCodeSelected,
} from '../../../profile/logic/useCandidateProfileForm';
import { useGetIExamMasterCode } from '../../../../api-refactored/user/useGetIExamMasterCode';
import { CandidateProfileFormDefaultValues } from '../../../../models';
import { ContactNumberFormItem } from '../../../../components/form-items/ContactNumberFormItem';
import {IExamMasterCodeItem} from "../../../../api/user/model";

const useNextOfKin = (nextOfKin: MobileNumberItems | undefined) =>
  getContactDisplayValue(false, nextOfKin?.countryCode.codeValueDescription, nextOfKin?.number);

interface NextOfKinContactDetailsProps {
  nextOfKinName?: string;
  nextOfKin?: MobileNumberItems;
  nextOfKinRelationship?: string;
  accordionBottomLine?: boolean;
  isLastItem?: boolean;
  form?: UseFormReturn<Record<string, any>>;
  isEditState?: boolean;
  formDefaultValues?: CandidateProfileFormDefaultValues;
  defaultExpanded?: boolean;
}

const NextOfKinContactDetails = ({
  nextOfKinName,
  nextOfKin,
  nextOfKinRelationship,
  accordionBottomLine,
  isEditState = false,
  isLastItem = false,
  form,
  formDefaultValues,
  defaultExpanded = true,
}: NextOfKinContactDetailsProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const phoneVal = useNextOfKin(nextOfKin);

  const {
    data: dataIEXAM,
    isLoading: isLoadingIEXAM,
    error: errorMasterCode,
    refetch: refetchMasterCode,
  } = useGetIExamMasterCode();

  return (
    <Accordion
      header={t('exam->>ReviewExam.NextOfKinDetails')}
      isLastItem={isLastItem}
      bottomLine={accordionBottomLine}
      defaultExpanded={defaultExpanded}
    >
      <Box className={classes.container}>
        <ToggleDisplayEditFormItem
          isEditState={isEditState}
          id="input-nextOfKinName"
          labelText={t('exam->>ReviewExam.Name')}
          valueText={fallbackDashString(nextOfKinName)}
          containerClassName={classes.formElementContainer}
          EditComponent={
            <TextFieldForm
                form={form}
              isInputTextUpperCase
              onChange={(e) => form?.setValue('nextOfKinName', e.target.value.toUpperCase())}
              label={t('exam->>ReviewExam.Name')}
              name="nextOfKinName"
              id="input-nextOfKinName"
              type="text"
              defaultValue={fallbackDashString(nextOfKinName)}
              error={!!form?.formState.errors.nextOfKinName}
              helperText={form?.formState.errors?.nextOfKinName?.message?.toString()}
              fullWidth
              inputProps={{ readOnly: disabledInput?.nextOfKinName }}
              isRequired
              placeholder={t('exam->>ReviewExam.EnterNextOfKinName')}
              regExpToBlock={nameRegExp}
            />
          }
        />
        <ToggleDisplayEditFormItem
          isEditState={isEditState}
          labelText={t('exam->>ReviewExam.RelationshipToCandidate')}
          valueText={fallbackDashString(nextOfKinRelationship)}
          containerClassName={classes.formElementContainer}
          id="input-select-nextOfKinRelationshipToCandidate"
          EditComponent={
            form &&
            dataIEXAM?.relationship && (
              <ControlledAutocomplete<IExamMasterCodeItem, false, false, undefined>
                label={t('exam->>ReviewExam.RelationshipToCandidate')}
                control={form.control}
                name="nextOfKinRelationshipToCandidate"
                id="input-select-nextOfKinRelationshipToCandidate"
                error={!!form.formState.errors.nextOfKinRelationshipToCandidate}
                helperText={form.formState.errors?.nextOfKinRelationshipToCandidate?.message?.toString()}
                loading={isLoadingIEXAM}
                loadingError={!!errorMasterCode}
                loadingErrorOnReFetch={refetchMasterCode}
                options={dataIEXAM ? dataIEXAM.relationship : []}
                getOptionLabel={getIExamDropdownItemLabel}
                placeholder={DROPDOWN_PLACEHOLDER}
                disabled={disabledInput?.nextOfKinRelationshipToCandidate}
                isRequired
              />
            )
          }
        />
        <ToggleDisplayEditFormItem
          isEditState={isEditState}
          labelText={t('exam->>ReviewExam.NextOfKinContactNo')}
          valueText={phoneVal}
          id="nextOfKinCountryCode"
          containerClassName={classes.formElementContainer}
          EditComponent={
            form && (
              <ContactNumberFormItem
                id="nextOfKinCountryCode"
                formHook={form}
                label={t('exam->>ReviewExam.NextOfKinContactNo')}
                countryCode={{
                  name: 'nextOfKinCountryCode',
                  error: !!form.formState.errors.nextOfKinCountryCode,
                  helperText: form.formState.errors?.nextOfKinCountryCode?.message?.toString(),
                  disabled: disabledInput?.nextOfKinCountryCode,
                }}
                countryCodeisOptionEqualToValue={getIExamMasterCodeSelected}
                number={{
                  name: 'nextOfKinContactNumber',
                  error: !!form.formState.errors.nextOfKinContactNumber,
                  helperText: form.formState.errors?.nextOfKinContactNumber?.message?.toString(),
                  disabled: disabledInput?.nextOfKinContactNumber,
                }}
                placeholder={t('exam->>ReviewExam.EnterNextOfKinContactNo')}
                isSkipTransform
                countryCodeDefaultValue={formDefaultValues?.nextOfKinCountryCode}
                numberDefaultValue={formDefaultValues?.nextOfKinContactNumber}
                isRequired
              />
            )
          }
        />
      </Box>
    </Accordion>
  );
};
export default NextOfKinContactDetails;
